/**
 * Haptic feedback helpers.
 *
 * Wraps @capacitor/haptics so we can call vibrations from anywhere without
 * caring whether we're running inside a Capacitor WebView or on plain web
 * (where the plugin is a no-op).
 *
 * Guidance:
 *   - light    → routine confirmations (mark complete, save, toggle)
 *   - medium   → destructive intents that will show a confirm dialog
 *   - heavy    → actual destructive completion, errors, warnings
 *   - success  → booking confirmed, payment received
 *   - error    → 4xx/5xx failures the user should feel
 */
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const safe = async (fn) => {
  if (!isNative()) return;
  try { await fn(); } catch { /* swallow — haptics are non-critical */ }
};

export const hapticLight  = () => safe(() => Haptics.impact({ style: ImpactStyle.Light }));
export const hapticMedium = () => safe(() => Haptics.impact({ style: ImpactStyle.Medium }));
export const hapticHeavy  = () => safe(() => Haptics.impact({ style: ImpactStyle.Heavy }));

export const hapticSuccess = () => safe(() => Haptics.notification({ type: NotificationType.Success }));
export const hapticWarning = () => safe(() => Haptics.notification({ type: NotificationType.Warning }));
export const hapticError   = () => safe(() => Haptics.notification({ type: NotificationType.Error }));
